import { useCallback } from "react";
import React from "react";
import { useParams } from "react-router-dom";

import { Rename } from "components/Prompts";
import { useIsViewerPermission, useModals, usePrompt, useRoleGuardHook } from "hooks";
import { useDocumentsActions, useTaskManageActions, useViewActions } from "store";
import { IItemToShare } from "store/types/deal/share";

import { EntityType, RoleName } from "../../../constants";
import { useWorkspace } from "../WorkspaceContext";

export interface UseWorkspaceDocumentHookParams {
  document: {
    id: number;
    title: string;
    deal_id: number;
    is_private: boolean;
  };
  refetchHandler?: () => Promise<void>;
}

export const useWorkspaceDocumentHook = ({ document, refetchHandler }: UseWorkspaceDocumentHookParams) => {
  const { deal_id: dealIdParam } = useParams<{ deal_id: string }>();
  const dealId = dealIdParam ? Number(dealIdParam) : null;
  const openPrompt = usePrompt();
  const { deleteModal } = useModals();
  const { setShareDialog } = useViewActions();
  const { updateDocumentAction, deleteDocumentAction } = useDocumentsActions();
  const { setCreateAction } = useTaskManageActions();
  const { dispatchDetail } = useWorkspace();

  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");

  const handleShare = useCallback(() => {
    setShareDialog({
      elements: [document.id],
      type: "document_id",
      shareName: document.title,
      dealId: dealId ?? null,
      itemToShare: document as unknown as IItemToShare,
      globalDoc: !dealId,
      cb: () => {
        refetchHandler?.() ?? Promise.resolve();
      }
    });
  }, [setShareDialog, document, dealId, refetchHandler]);

  const onConvertToGeneral = useCallback(async () => {
    await updateDocumentAction({ id: document.id, make_global: true });
    await refetchHandler?.();
  }, [document, updateDocumentAction, refetchHandler]);

  const handleAddTask = useCallback(() => {
    setCreateAction({
      isOpen: true,
      entity_id: document.id,
      entity_type: "document"
    });
  }, [setCreateAction, document.id]);

  const handleDelete = useCallback(
    async (id: number) => {
      if (!document.id) return;
      await deleteDocumentAction(id);
      dispatchDetail({
        key: "delete",
        id: document.id,
        entityType: EntityType.DOCUMENT
      });
    },
    [deleteDocumentAction, document.id, dispatchDetail]
  );

  const handleDeleteDocument = useCallback(
    (id: number) => {
      if (!document.id) return;
      return deleteModal({
        element: "Delete item permanently?",
        message: "This cannot be undone",
        onPressSuccess: "Delete permanently",
        action: async () => await handleDelete(id)
      });
    },
    [document.id, deleteModal, handleDelete]
  );

  const handleChangePrivacy = useCallback(
    async (is_private: boolean) => {
      await updateDocumentAction({ id: document.id, is_private });
      dispatchDetail({
        key: "patch",
        id: document.id,
        entityType: EntityType.DOCUMENT,
        patch: { is_private }
      });
    },
    [document.id, updateDocumentAction, dispatchDetail]
  );

  const handleRename = useCallback(
    async (menuTriggerRef: React.RefObject<HTMLButtonElement>, onMenuClose: () => void) => {
      if (!document.id) return;
      const trigger = menuTriggerRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const anchorPosition = {
        top: Math.round(triggerRect.bottom),
        left: Math.round(triggerRect.left)
      };

      onMenuClose();

      const title = await openPrompt({
        Component: Rename,
        componentType: "popover",
        anchorPosition,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        props: { title: document.title }
      });
      if (title) {
        await updateDocumentAction({ id: document.id, title });
        dispatchDetail({
          key: "patch",
          id: document.id,
          entityType: EntityType.DOCUMENT,
          patch: { title }
        });
      }
    },
    [document.id, document.title, openPrompt, updateDocumentAction, dispatchDetail]
  );

  const isAvailableRoleWithPermissions = isAnalystDealsViewerPermission
    ? [RoleName.Admin]
    : [RoleName.Admin, RoleName.Analyst];

  const handleShareWithRole = useRoleGuardHook(handleShare, isAvailableRoleWithPermissions);
  const handleAddTaskWithRole = useRoleGuardHook(handleAddTask, isAvailableRoleWithPermissions);
  const onDeleteDocumentWithRole = useRoleGuardHook(handleDeleteDocument, isAvailableRoleWithPermissions);
  const handleChangePrivacyWithRole = useRoleGuardHook(handleChangePrivacy, isAvailableRoleWithPermissions);
  const handleRenameWithRole = useRoleGuardHook(handleRename, isAvailableRoleWithPermissions);
  const onConvertToGeneralWithRole = useRoleGuardHook(onConvertToGeneral, isAvailableRoleWithPermissions);

  return {
    handleShare: handleShareWithRole,
    handleAddTask: handleAddTaskWithRole,
    handleDeleteDocument: onDeleteDocumentWithRole,
    handleChangePrivacy: handleChangePrivacyWithRole,
    handleRename: handleRenameWithRole,
    handleConvertToGeneral: onConvertToGeneralWithRole
  };
};
